import { DateTime } from 'luxon';
import { z } from 'zod';
import { routableCareCallTypeSchema } from '../care/care-call-types';
import { apiEnvelope } from '../envelope';
import { memberIdSchema } from '../identity/legacy-identity-id';
import { careAppointmentModalitySchema } from '../care/appointments';
import { ClinicianRole } from '../programs/schema';

// Opaque staff Chime ExternalUserId, sourced from the assigned specialist's
// `care_specialists.counsellor_key` row — never client-supplied.
export const telemedicineCounsellorKeySchema = z.string().min(1);

export const telemedicineSurfaceSchema = z.enum(['profile-care']);
export type TelemedicineSurface = z.infer<typeof telemedicineSurfaceSchema>;

export const telemedicineProviderSchema = z.enum(['chime']);
export type TelemedicineProvider = z.infer<typeof telemedicineProviderSchema>;

// The server resolves the room from the member's own upcoming appointment;
// `counsellorKey` is a transitional field older bundles still send and the
// server ignores — it is never an input to routing or authorisation. Remove
// it once no supported OTA bundle sends it (the current bundle already omits
// it, so the field can go with the next native release's minimum-bundle bump).
// `appointmentId` names the exact member-owned appointment the member tapped:
// with several upcoming bookings whose Join promotions can overlap, the earliest-
// unlapsed fallback alone could open the wrong call. Optional so older bundles
// (which omit it) keep the fallback behaviour; it is an authorisation *narrowing*
// only — the server still verifies ownership and that the call has not lapsed.
export const telemedicineCallSetupRequestSchema = z
  .object({
    surface: telemedicineSurfaceSchema,
    counsellorKey: z.string().optional(),
    appointmentId: z.string().uuid().optional(),
  })
  .strict();
export type TelemedicineCallSetupRequest = z.infer<typeof telemedicineCallSetupRequestSchema>;

// Chime media-placement values are not all URL-shaped (AudioHostUrl is
// `host:port`), so the fields validate as non-empty strings. The screen-share
// URLs are legacy Chime fields the meetings SDK may omit; the native clients
// only require the audio/signalling values.
export const telemedicineChimeMediaPlacementSchema = z
  .object({
    audioHostUrl: z.string().min(1),
    audioFallbackUrl: z.string().min(1),
    screenDataUrl: z.string().min(1).optional(),
    screenSharingUrl: z.string().min(1).optional(),
    screenViewingUrl: z.string().min(1).optional(),
    signallingUrl: z.string().min(1),
    turnControlUrl: z.string().min(1),
  })
  .strict();
export type TelemedicineChimeMediaPlacement = z.infer<typeof telemedicineChimeMediaPlacementSchema>;

export const telemedicineChimeMeetingSchema = z
  .object({
    meetingId: z.string().min(1),
    mediaRegion: z.string().min(1),
    mediaPlacement: telemedicineChimeMediaPlacementSchema,
  })
  .strict();
export type TelemedicineChimeMeeting = z.infer<typeof telemedicineChimeMeetingSchema>;

export const telemedicineChimeAttendeeSchema = z
  .object({
    attendeeId: z.string().min(1),
    externalUserId: z.string().min(1),
    joinToken: z.string().min(1),
  })
  .strict();
export type TelemedicineChimeAttendee = z.infer<typeof telemedicineChimeAttendeeSchema>;

export const telemedicineCallSetupDataSchema = z
  .object({
    callSessionId: z.string().uuid(),
    // The appointment the server resolved for this call — echoed back so the
    // client knows which appointment it joined even when it did not pass one
    // (the "join now" / incoming-call path resolves it server-side). Optional
    // for deploy-window skew (a newer client must still parse an older server's
    // response); the server always populates it.
    appointmentId: z.string().uuid().optional(),
    provider: telemedicineProviderSchema,
    counsellor: z
      .object({
        key: telemedicineCounsellorKeySchema,
        displayName: z.string().min(1),
        emailMasked: z.string().min(1),
      })
      .strict(),
    meeting: telemedicineChimeMeetingSchema,
    attendee: telemedicineChimeAttendeeSchema,
    expiresAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type TelemedicineCallSetupData = z.infer<typeof telemedicineCallSetupDataSchema>;

export const telemedicineCallSetupResponseSchema = apiEnvelope(telemedicineCallSetupDataSchema);
export type TelemedicineCallSetupResponse = z.infer<typeof telemedicineCallSetupResponseSchema>;

export const telemedicineMeetingStartRequestSchema = z
  .object({
    appointmentId: z.string().uuid(),
  })
  .strict();
export type TelemedicineMeetingStartRequest = z.infer<typeof telemedicineMeetingStartRequestSchema>;

export const telemedicineMeetingStartDataSchema = z
  .object({
    counsellorKey: telemedicineCounsellorKeySchema,
    callSessionId: z.string().uuid(),
    meeting: telemedicineChimeMeetingSchema,
    attendee: telemedicineChimeAttendeeSchema,
    expiresAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type TelemedicineMeetingStartData = z.infer<typeof telemedicineMeetingStartDataSchema>;

export const telemedicineMeetingStartResponseSchema = apiEnvelope(
  telemedicineMeetingStartDataSchema,
);
export type TelemedicineMeetingStartResponse = z.infer<
  typeof telemedicineMeetingStartResponseSchema
>;

export const telemedicineCallEndRequestSchema = z
  .object({
    callSessionId: z.string().uuid(),
  })
  .strict();
export type TelemedicineCallEndRequest = z.infer<typeof telemedicineCallEndRequestSchema>;

// `ended` = the call is over (meeting deleted); `left` = the member left but
// the call stays active for the counsellor; `kept-active` = the counsellor
// left with no member present, so the call stays joinable by both parties.
export const telemedicineCallEndDataSchema = z
  .object({
    callSessionId: z.string().uuid(),
    result: z.enum(['ended', 'already-ended', 'left', 'kept-active']),
    /** True when the member sent at least one in-call heartbeat in this session. */
    memberEverPresentInSession: z.boolean().optional(),
  })
  .strict();
export type TelemedicineCallEndData = z.infer<typeof telemedicineCallEndDataSchema>;

export const telemedicineCallEndResponseSchema = apiEnvelope(telemedicineCallEndDataSchema);
export type TelemedicineCallEndResponse = z.infer<typeof telemedicineCallEndResponseSchema>;

export const telemedicineCallHeartbeatMemberPresencePhaseSchema = z.enum([
  'waiting-room',
  'in-call',
]);
export type TelemedicineCallHeartbeatMemberPresencePhase = z.infer<
  typeof telemedicineCallHeartbeatMemberPresencePhaseSchema
>;

export const telemedicineCallHeartbeatRequestSchema = z
  .object({
    callSessionId: z.string().uuid(),
    /** Member-only: waiting-room stamps refresh `member_waiting_room_at`; in-call
     * (or omitted) stamps `member_last_seen_at` and clears the waiting-room mark. */
    memberPresencePhase: telemedicineCallHeartbeatMemberPresencePhaseSchema.optional(),
  })
  .strict();
export type TelemedicineCallHeartbeatRequest = z.infer<
  typeof telemedicineCallHeartbeatRequestSchema
>;

export const telemedicineCallHeartbeatDataSchema = z
  .object({
    callSessionId: z.string().uuid(),
    result: z.enum(['ok', 'not-active']),
  })
  .strict();
export type TelemedicineCallHeartbeatData = z.infer<typeof telemedicineCallHeartbeatDataSchema>;

export const telemedicineCallHeartbeatResponseSchema = apiEnvelope(
  telemedicineCallHeartbeatDataSchema,
);
export type TelemedicineCallHeartbeatResponse = z.infer<
  typeof telemedicineCallHeartbeatResponseSchema
>;

// Member-facing presence snapshot for the appointment card: `active` is null
// when no live call exists for the member's counsellor (or it is bound to a
// different member). `counsellorInCall` reflects a recent presence heartbeat.
export const telemedicineActiveCallDataSchema = z.object({
  active: z
    .object({
      callSessionId: z.string().uuid(),
      counsellorInCall: z.boolean(),
      memberInCall: z.boolean(),
      expiresAt: z.string().datetime({ offset: true }),
      memberDeclinedAt: z.string().datetime({ offset: true }).nullable(),
    })
    .nullable(),
});

export const telemedicineCallDeclineDeliverySchema = z.enum(['immediate', 'outbox-drain']);
export type TelemedicineCallDeclineDelivery = z.infer<typeof telemedicineCallDeclineDeliverySchema>;

export const telemedicineCallDeclineRequestSchema = z
  .object({
    callSessionId: z.string().uuid(),
    delivery: telemedicineCallDeclineDeliverySchema,
  })
  .strict();
export type TelemedicineCallDeclineRequest = z.infer<typeof telemedicineCallDeclineRequestSchema>;

export const telemedicineCallDeclineDataSchema = z
  .object({
    declined: z.literal(true),
    alreadyDeclined: z.boolean(),
  })
  .strict();
export type TelemedicineCallDeclineData = z.infer<typeof telemedicineCallDeclineDataSchema>;

export const telemedicineCallDeclineResponseSchema = apiEnvelope(telemedicineCallDeclineDataSchema);
export type TelemedicineCallDeclineResponse = z.infer<typeof telemedicineCallDeclineResponseSchema>;
export type TelemedicineActiveCallData = z.infer<typeof telemedicineActiveCallDataSchema>;

export const telemedicineActiveCallResponseSchema = apiEnvelope(telemedicineActiveCallDataSchema);
export type TelemedicineActiveCallResponse = z.infer<typeof telemedicineActiveCallResponseSchema>;

export const TELEMEDICINE_FEEDBACK_NOTES_MAX_LENGTH = 1000;

export const telemedicineCallFeedbackRequestSchema = z
  .object({
    callSessionId: z.string().uuid(),
    rating: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    notes: z.string().min(1).max(TELEMEDICINE_FEEDBACK_NOTES_MAX_LENGTH).optional(),
  })
  .strict();
export type TelemedicineCallFeedbackRequest = z.infer<typeof telemedicineCallFeedbackRequestSchema>;

export const telemedicineCallFeedbackDataSchema = z
  .object({
    id: z.string().uuid(),
    callSessionId: z.string().uuid(),
    rating: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    replayed: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type TelemedicineCallFeedbackData = z.infer<typeof telemedicineCallFeedbackDataSchema>;

export const telemedicineCallFeedbackResponseSchema = apiEnvelope(
  telemedicineCallFeedbackDataSchema,
);
export type TelemedicineCallFeedbackResponse = z.infer<
  typeof telemedicineCallFeedbackResponseSchema
>;

// Staff-facing upcoming appointments for the resolved specialist (Atlas
// telemedicine surface). `member` carries the Galapagos opaque id, email, and
// verified legal name when one exists — the same identity slice the member
// list exposes, never borrowed backend2 demographics.
export const careAppointmentAttendanceOutcomeSchema = z.enum(['attended', 'not_attended']);
export type CareAppointmentAttendanceOutcome = z.infer<
  typeof careAppointmentAttendanceOutcomeSchema
>;

export const careAppointmentAttendanceReasonSchema = z.enum([
  'no_show',
  'joined_not_participated',
  'technical_issue',
  'other',
]);
export type CareAppointmentAttendanceReason = z.infer<typeof careAppointmentAttendanceReasonSchema>;

export const careAppointmentAttendanceSchema = z.object({
  outcome: careAppointmentAttendanceOutcomeSchema,
  reason: careAppointmentAttendanceReasonSchema.nullable(),
  markedAt: z.string().datetime({ offset: true }),
});
export type CareAppointmentAttendance = z.infer<typeof careAppointmentAttendanceSchema>;

export const telemedicineRecordAttendanceRequestSchema = z
  .object({
    outcome: careAppointmentAttendanceOutcomeSchema,
    reason: careAppointmentAttendanceReasonSchema.optional(),
  })
  .strict()
  .superRefine((body, ctx) => {
    if (body.outcome === 'attended' && body.reason !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'reason must be omitted when outcome is attended',
        path: ['reason'],
      });
    }
  });
export type TelemedicineRecordAttendanceRequest = z.infer<
  typeof telemedicineRecordAttendanceRequestSchema
>;

export const telemedicineRecordAttendanceDataSchema = z.object({
  appointmentId: z.string().uuid(),
  attendance: careAppointmentAttendanceSchema,
  pointsEarned: z.number().int().nonnegative(),
  /** Present when the API soft-end claim closed the live call; omitted on older clients (ADR-135). */
  callEnded: z.boolean().optional(),
});
export type TelemedicineRecordAttendanceData = z.infer<
  typeof telemedicineRecordAttendanceDataSchema
>;

export const telemedicineRecordAttendanceResponseSchema = apiEnvelope(
  telemedicineRecordAttendanceDataSchema,
);
export type TelemedicineRecordAttendanceResponse = z.infer<
  typeof telemedicineRecordAttendanceResponseSchema
>;

export const telemedicineStaffAppointmentSchema = z.object({
  id: z.string().uuid(),
  startsAt: z.string().datetime({ offset: true }),
  durationMinutes: z.number().int().positive(),
  status: z.enum(['booked', 'completed']),
  specialistId: z.string().uuid(),
  callType: routableCareCallTypeSchema.nullable(),
  /** Visit modality when stored; omitted on legacy-import null rows (ADR-135). */
  modality: careAppointmentModalitySchema.optional(),
  // Optional for deploy skew: an older API may omit the field before this Lambda
  // rolls out; Atlas treats absence as false (ADR-135 Rule 2 carve-out — web SPA).
  memberInCall: z.boolean().optional(),
  legacyAppointmentId: z.number().int().positive().nullable().optional(),
  /**
   * The mirrored Healthie appointment id, so Atlas can recognise the Pelago 1.0
   * row that 1.0 ingests from 2.0's own Healthie write-back. Optional for deploy
   * skew (ADR-135); Atlas treats absence as no match.
   */
  healthieAppointmentId: z.string().min(1).nullable().optional(),
  member: z
    .object({
      id: memberIdSchema,
      email: z.string().nullable(),
      legalFirstName: z.string().nullable(),
      legalLastName: z.string().nullable(),
    })
    .strict(),
});
export type TelemedicineStaffAppointment = z.infer<typeof telemedicineStaffAppointmentSchema>;

const STAFF_APPOINTMENTS_MAX_RANGE_DAYS = 42;

/** Calendar-day span for `[from, to)` using the offset zone carried on `from`. */
export function staffAppointmentsQueryCalendarDaySpan(fromIso: string, toIso: string): number {
  const from = DateTime.fromISO(fromIso, { setZone: true });
  const to = DateTime.fromISO(toIso, { setZone: true });
  if (!from.isValid || !to.isValid) {
    return Number.NaN;
  }
  const zone = from.zone;
  return to.setZone(zone).startOf('day').diff(from.setZone(zone).startOf('day'), 'days').days;
}

export const telemedicineStaffAppointmentsQuerySchema = z
  .object({
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
  })
  .superRefine((value, ctx) => {
    const hasFrom = value.from != null;
    const hasTo = value.to != null;
    if (hasFrom !== hasTo) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'from and to must both be present or both be omitted',
        path: hasFrom ? ['to'] : ['from'],
      });
      return;
    }
    if (!hasFrom) return;
    const fromMs = Date.parse(value.from ?? '');
    const toMs = Date.parse(value.to ?? '');
    if (toMs <= fromMs) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'to must be after from',
        path: ['to'],
      });
    }
    const calendarDaySpan = staffAppointmentsQueryCalendarDaySpan(value.from ?? '', value.to ?? '');
    if (calendarDaySpan > STAFF_APPOINTMENTS_MAX_RANGE_DAYS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `range must not exceed ${STAFF_APPOINTMENTS_MAX_RANGE_DAYS} calendar days`,
        path: ['to'],
      });
    }
  });
export type TelemedicineStaffAppointmentsQuery = z.infer<
  typeof telemedicineStaffAppointmentsQuerySchema
>;

export const telemedicineStaffAppointmentsDataSchema = z.object({
  appointments: z.array(telemedicineStaffAppointmentSchema),
});
export type TelemedicineStaffAppointmentsData = z.infer<
  typeof telemedicineStaffAppointmentsDataSchema
>;

export const telemedicineStaffAppointmentsResponseSchema = apiEnvelope(
  telemedicineStaffAppointmentsDataSchema,
);
export type TelemedicineStaffAppointmentsResponse = z.infer<
  typeof telemedicineStaffAppointmentsResponseSchema
>;

// Staff-facing active-specialist roster (Atlas scheduling surfaces): display
// fields plus the Galapagos `internal_users.id` the specialist belongs to — no
// Healthie provider ids or counsellor keys leave the server. The internal-user
// id is what lets a staff surface correlate a scheduling target with the
// member's care team (whose assignments are keyed by internal user, not by
// `care_specialists.id`).
export const telemedicineSpecialistSchema = z.object({
  id: z.string().uuid(),
  internalUserId: z.string().uuid(),
  displayName: z.string().min(1),
  displayRole: z.string().min(1),
  /** Clinical role for care-team writes when the target is not yet assigned. */
  clinicalRole: ClinicianRole,
  /** True on exactly the caller's own row, so a picker whose default is
   * "me" can still resolve which specialist that is. */
  isSelf: z.boolean(),
  /** On P&P reads only: whether the specialist is on the member's care team
   * (those come first in the list). */
  onCareTeam: z.boolean().optional(),
});
export type TelemedicineSpecialist = z.infer<typeof telemedicineSpecialistSchema>;

export const telemedicineSpecialistsDataSchema = z
  .object({
    specialists: z.array(telemedicineSpecialistSchema),
  })
  .strict();
export type TelemedicineSpecialistsData = z.infer<typeof telemedicineSpecialistsDataSchema>;

export const telemedicineSpecialistsResponseSchema = apiEnvelope(telemedicineSpecialistsDataSchema);
export type TelemedicineSpecialistsResponse = z.infer<typeof telemedicineSpecialistsResponseSchema>;

export const telemedicineAdhocCallOutcomeSchema = z.enum(['answered', 'voicemail', 'unanswered']);
export type TelemedicineAdhocCallOutcome = z.infer<typeof telemedicineAdhocCallOutcomeSchema>;

/**
 * On what footing a staff caller was admitted to place an adhoc call. Written
 * to the call row and emitted as a telemetry dimension; never an input to the
 * admission decision itself. The Drizzle CHECK derives from this enum.
 */
export const CARE_ADHOC_CALL_ACCESS_BASES = [
  'primary_assignee',
  'care_team_assignee',
  'recorded_justification',
  'booked_specialist',
] as const;
export const careAdhocCallAccessBasisSchema = z.enum(CARE_ADHOC_CALL_ACCESS_BASES);
export type CareAdhocCallAccessBasis = z.infer<typeof careAdhocCallAccessBasisSchema>;

/** UI hint only — staff must confirm the outcome at hang-up. */
export function suggestAdhocPstnCallOutcome(
  remoteConnected: boolean,
): TelemedicineAdhocCallOutcome {
  return remoteConnected ? 'answered' : 'unanswered';
}

export const telemedicineAdhocCallStartRequestSchema = z
  .object({
    memberId: memberIdSchema,
    /** When set, the call is against this booked phone appointment. */
    appointmentId: z.string().uuid().optional(),
  })
  .strict();
export type TelemedicineAdhocCallStartRequest = z.infer<
  typeof telemedicineAdhocCallStartRequestSchema
>;

export const telemedicineAdhocCallStartDataSchema = z.object({
  appointmentId: z.string().uuid(),
  meeting: telemedicineChimeMeetingSchema,
  attendee: telemedicineChimeAttendeeSchema,
  expiresAt: z.string().datetime({ offset: true }),
});
export type TelemedicineAdhocCallStartData = z.infer<typeof telemedicineAdhocCallStartDataSchema>;

export const telemedicineAdhocCallStartResponseSchema = apiEnvelope(
  telemedicineAdhocCallStartDataSchema,
);
export type TelemedicineAdhocCallStartResponse = z.infer<
  typeof telemedicineAdhocCallStartResponseSchema
>;

export const telemedicineAdhocCallStopRequestSchema = z
  .object({
    appointmentId: z.string().uuid(),
    meetingId: z.string().min(1),
    outcome: telemedicineAdhocCallOutcomeSchema,
  })
  .strict();
export type TelemedicineAdhocCallStopRequest = z.infer<
  typeof telemedicineAdhocCallStopRequestSchema
>;

export const telemedicineAdhocCallStopDataSchema = z.object({
  appointmentId: z.string().uuid(),
  result: z.enum(['ended', 'already-ended']),
});
export type TelemedicineAdhocCallStopData = z.infer<typeof telemedicineAdhocCallStopDataSchema>;

export const telemedicineAdhocCallStopResponseSchema = apiEnvelope(
  telemedicineAdhocCallStopDataSchema,
);
export type TelemedicineAdhocCallStopResponse = z.infer<
  typeof telemedicineAdhocCallStopResponseSchema
>;

export const telemedicineAdhocCallAvailabilityDataSchema = z.object({
  configured: z.boolean(),
});
export type TelemedicineAdhocCallAvailabilityData = z.infer<
  typeof telemedicineAdhocCallAvailabilityDataSchema
>;

export const telemedicineAdhocCallAvailabilityResponseSchema = apiEnvelope(
  telemedicineAdhocCallAvailabilityDataSchema,
);
export type TelemedicineAdhocCallAvailabilityResponse = z.infer<
  typeof telemedicineAdhocCallAvailabilityResponseSchema
>;
